import { useStyletron } from '@thanx/uikit/theme'
import { getLocations } from 'actions/location'
import ProtectedPageHeader from 'components/ProtectedPageHeader'
import ScrollingTabNav from 'components/Tabs/ScrollingTabNav'
import TabItem from 'components/Tabs/TabItem'
import TabNav from 'components/Tabs/TabNav'
import TabPane from 'components/Tabs/TabPane'
import { useHashTab } from 'components/Tabs/useHashTab'
import { LocationContextProvider } from 'contexts/LocationContext'
import useCurrentMerchant from 'hooks/useCurrentMerchant'
import useDispatch from 'hooks/useDispatch'
import useFlag from 'hooks/useFlag'
import { buildTranslate } from 'locales'
import isEmpty from 'lodash/isEmpty'
import React, { useEffect } from 'react'
import Tab from 'react-bootstrap-five/Tab'
import Helmet from 'react-helmet'
import { useSelector } from 'react-redux'
import { selectCurrentMerchantLocations } from 'selectors/location'
import { selectCurrentMerchantUser } from 'selectors/merchantUser'
import { history } from 'store'
import AverageCheckByHandoffModeTab from './AverageCheckByHandoffModeTab'
import AverageCheckByLocationTab from './AverageCheckByLocationTab'
import AverageCheckOverviewTab from './AverageCheckOverviewTab'
import AverageRevenuePerLocationPerMonthTab from './AverageRevenuePerLocationPerMonthTab'
import RevenueAtSelectLocationsTab from './RevenueAtSelectLocationsTab'
import RevenueByClientTab from './RevenueByClientTab'
import RevenueByHandoffTab from './RevenueByHandoffTab'
import RevenueByUtmTab from './RevenueByUtmTab'
import RevenueTab from './RevenueTab'

const t = buildTranslate('revenue')

enum Tabs {
  REVENUE = 'revenue',
  REVENUE_AT_SELECT_LOCATIONS = 'revenue_at_select_locations',
  REVENUE_BY_CLIENT = 'revenue_by_client',
  REVENUE_BY_HANDOFF = 'revenue_by_handoff',
  AVERAGE_REVENUE_PER_LOCATION_PER_MONTH = 'average_revenue_per_location_per_month',
  AVERAGE_CHECK_OVERVIEW = 'average_check_overview',
  AVERAGE_CHECK_BY_LOCATION = 'average_check_by_location',
  AVERAGE_CHECK_BY_HANDOFF_MODE = 'average_check_by_handoff_mode',
  REVENUE_BY_UTM = 'revenue_by_utm',
}

// Tab order for the Revenue report (/reports/revenue) when the
// mx-promote-insights-lab-reports flag is on: Revenue at select
// location(s) (1st/default), Digital revenue by client, Digital revenue by
// handoff mode, Average revenue per location per month, Average check
// overview, Average check by location, Average check by handoff mode,
// Revenue by UTM (8 tabs total).
//
// When the flag is off, this page renders the bespoke hand-built "Revenue"
// report (Tabs.REVENUE / RevenueTab.tsx, backed by the real stats/chart/
// table components in ./components) as the 1st/default tab instead. Revenue
// by UTM is pre-existing and unaffected by the flag: it renders as the
// last tab in both states.
// The two tabs the flag-off page renders (master's shape). Every other tab in
// the enum exists only with the flag on, and Tabs.REVENUE only exists with it
// off.
const FLAG_OFF_TABS: Tabs[] = [Tabs.REVENUE, Tabs.REVENUE_BY_UTM]

const visibleTabs = (isFlagEnabled: boolean): Tabs[] =>
  isFlagEnabled
    ? Object.values(Tabs).filter(tab => tab !== Tabs.REVENUE)
    : FLAG_OFF_TABS

const defaultTab = (isFlagEnabled: boolean): Tabs =>
  isFlagEnabled ? Tabs.REVENUE_AT_SELECT_LOCATIONS : Tabs.REVENUE

const RevenueReport: React.FC = () => {
  const isPromoteInsightsLabReportsEnabled = useFlag(
    'mx-promote-insights-lab-reports',
    false
  )
  // Resolved from the URL hash against the tabs actually on screen. `useFlag`
  // reads false both when the flag is off and while LaunchDarkly is still
  // evaluating, so the visible set and the default both change once the real
  // value lands — useHashTab keys its effect on exactly those two, so that
  // late arrival re-resolves. Seeding a useState at mount instead used to
  // capture Tabs.REVENUE with the flag on, which has no Nav.Item or TabPane in
  // the flag-on branch: activeKey matched nothing, no tab looked selected, the
  // content area rendered empty and the page title came from the wrong tab.
  const tabs = visibleTabs(isPromoteInsightsLabReportsEnabled)
  const [activeTab, setActiveTab] = useHashTab(
    tabs,
    defaultTab(isPromoteInsightsLabReportsEnabled)
  )

  const handleSwitchTab = (tab: Tabs) => {
    history.replace({ ...history.location, hash: tab })
    setActiveTab(tab)
  }
  const merchant = useCurrentMerchant()!
  const merchantUser = useSelector(selectCurrentMerchantUser)
  const merchantId = useSelector((state: any) => state.auth.current_merchant_id)
  const roles = merchantUser?.rolesForMerchantId(merchantId)
  const locations = useSelector(selectCurrentMerchantLocations)
  const dispatch = useDispatch()
  const [css] = useStyletron()

  useEffect(() => {
    dispatch(getLocations({ coming_soon: false }))
  }, [dispatch])

  if (isEmpty(locations)) return null

  const pageTitle = t(`tabs.${activeTab}.title_tag`)

  return (
    <ProtectedPageHeader
      title={t('title')}
      description=""
      resource="LocationReports"
      action="access"
    >
      <Helmet>
        <title>{pageTitle}</title>
      </Helmet>
      <LocationContextProvider
        initialLocation={roles?.includes('manager') ? locations[0] : null}
      >
        <Tab.Container
          activeKey={activeTab}
          transition={false}
          onSelect={(tab: string | null) => {
            if (!tab) return
            handleSwitchTab(tab as Tabs)
          }}
        >
          {/* The nav component itself is branched on the flag, not just its
              items: eight tabs wrap onto a second row at common widths, which
              is what ScrollingTabNav exists for, while the flag-off row must
              stay byte-identical to master — so that branch keeps TabNav. */}
          {isPromoteInsightsLabReportsEnabled ? (
            <ScrollingTabNav ariaLabel={t('title')}>
              <TabItem
                state={Tabs.REVENUE_AT_SELECT_LOCATIONS}
                title={t('tabs.revenue_at_select_locations.title')}
              />
              <TabItem
                state={Tabs.REVENUE_BY_CLIENT}
                title={t('tabs.revenue_by_client.title')}
              />
              <TabItem
                state={Tabs.REVENUE_BY_HANDOFF}
                title={t('tabs.revenue_by_handoff.title')}
              />
              <TabItem
                state={Tabs.AVERAGE_REVENUE_PER_LOCATION_PER_MONTH}
                title={t('tabs.average_revenue_per_location_per_month.title')}
              />
              <TabItem
                state={Tabs.AVERAGE_CHECK_OVERVIEW}
                title={t('tabs.average_check_overview.title')}
              />
              <TabItem
                state={Tabs.AVERAGE_CHECK_BY_LOCATION}
                title={t('tabs.average_check_by_location.title')}
              />
              <TabItem
                state={Tabs.AVERAGE_CHECK_BY_HANDOFF_MODE}
                title={t('tabs.average_check_by_handoff_mode.title')}
              />
              <TabItem
                state={Tabs.REVENUE_BY_UTM}
                title={t('tabs.revenue_by_utm.title')}
              />
            </ScrollingTabNav>
          ) : (
            <TabNav>
              <TabItem state={Tabs.REVENUE} title={t('tabs.revenue.title')} />
              <TabItem
                state={Tabs.REVENUE_BY_UTM}
                title={t('tabs.revenue_by_utm.title')}
              />
            </TabNav>
          )}
          <Tab.Content
            className={`pb-l h-100 grey-05-bkg border-top-1 grey-20-border ${css(
              {
                minHeight: `calc(100% - 50px)`,
              }
            )}`}
          >
            {isPromoteInsightsLabReportsEnabled ? (
              <>
                <TabPane state={Tabs.REVENUE_AT_SELECT_LOCATIONS} mountOnEnter>
                  <RevenueAtSelectLocationsTab />
                </TabPane>
                <TabPane state={Tabs.REVENUE_BY_CLIENT} mountOnEnter>
                  <RevenueByClientTab />
                </TabPane>
                <TabPane state={Tabs.REVENUE_BY_HANDOFF} mountOnEnter>
                  <RevenueByHandoffTab />
                </TabPane>
                <TabPane
                  state={Tabs.AVERAGE_REVENUE_PER_LOCATION_PER_MONTH}
                  mountOnEnter
                >
                  <AverageRevenuePerLocationPerMonthTab />
                </TabPane>
                <TabPane state={Tabs.AVERAGE_CHECK_OVERVIEW} mountOnEnter>
                  <AverageCheckOverviewTab />
                </TabPane>
                <TabPane state={Tabs.AVERAGE_CHECK_BY_LOCATION} mountOnEnter>
                  <AverageCheckByLocationTab />
                </TabPane>
                <TabPane
                  state={Tabs.AVERAGE_CHECK_BY_HANDOFF_MODE}
                  mountOnEnter
                >
                  <AverageCheckByHandoffModeTab />
                </TabPane>
              </>
            ) : (
              <TabPane state={Tabs.REVENUE}>
                <RevenueTab merchant={merchant} />
              </TabPane>
            )}
            {/* Shared across both branches, so the prop is flag-conditional
                rather than unconditional: with the flag off this is one of two
                tabs and master mounts it eagerly, and that shape has to stay
                byte-identical. With the flag on it becomes a hidden eighth tab
                behind a promoted default, and mounting its embed there costs an
                embed_url fetch and an iframe nobody opened. */}
            <TabPane
              state={Tabs.REVENUE_BY_UTM}
              mountOnEnter={isPromoteInsightsLabReportsEnabled}
            >
              <RevenueByUtmTab />
            </TabPane>
          </Tab.Content>
        </Tab.Container>
      </LocationContextProvider>
    </ProtectedPageHeader>
  )
}

export default RevenueReport
